import { useEffect, useMemo, useRef, useState } from 'react'
import { Icon } from '../../../components/icons/Icon'
import { StatusPill } from '../../../components/StatusPill'
import { InlineAlert } from '../../../components/InlineAlert'
import { FocusTrapDialog } from '../../../components/FocusTrapDialog'
import { ForwardLookingCell } from '../../../design-system/list-view/cells/ForwardLookingCell'
import { RowActionsMenu, type RowAction } from '../../../design-system/list-view/RowActionsMenu'
import { lifecycleStatusPill } from './lifecycleStatus'
import {
  approveParameter,
  createParameterDraft,
  listParameters,
  publishParameter,
  submitParameterForReview,
} from '../../../api/cartridgeStudio'
import { ApiError } from '../../../api/problem'
import type { ParameterView } from '../../../types/cartridgeStudio'
import './ParameterEditor.css'

export interface ParameterEditorProps {
  ruleSetId: string
}

/**
 * DoR §3 AC2 / design doc §4.1 — the Parameter grid + effective-date lane + as-of preview.
 * `ParameterView` carries no citation field (design doc §0 finding, `ForwardLookingCell` applied).
 *
 * <p>Row-level maker-checker (review-submissions/approvals/publications) is exposed via
 * `RowActionsMenu`, scoped to that Parameter's own id — a RuleSet's publish state and a Parameter
 * row's publish state are independent (design doc §4.1).
 *
 * <p><b>Disclosed gap — "isAuthor" cannot be determined client-side.</b> `TenantContextBindingFilter`
 * resolves the caller's `AppUser.id` server-side only (from the JWT `sub`, never exposed to the
 * frontend as a comparable id here — no `/me`-actor-id endpoint exists in this story's scope); this
 * screen does not fabricate a match against `createdBy`. Approve/Publish render enabled for every
 * row; the real, server-enforced 403 (`CartridgeSelfApprovalException`) is what actually blocks a
 * self-approval, surfaced inline exactly like every other failure path here — the design doc's own
 * framing ("UI disablement is a courtesy, not the enforcement — the 403 is") is honoured by leaning
 * on the 403 alone rather than guessing at authorship.
 */
export function ParameterEditor({ ruleSetId }: ParameterEditorProps) {
  const [parameters, setParameters] = useState<ParameterView[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [rowError, setRowError] = useState<string | null>(null)
  const [busyRowId, setBusyRowId] = useState<string | null>(null)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [selectedKey, setSelectedKey] = useState<string | null>(null)
  const [asOf, setAsOf] = useState(() => new Date().toISOString().slice(0, 10))

  useEffect(() => {
    // React 18 StrictMode double-invokes this effect in dev; the `controller.signal.aborted`
    // checks below prevent the first (aborted) run's stale rejection from clobbering the second
    // (real) run's successful state — see ProgramWorkspaceScreen's effect for the full rationale.
    const controller = new AbortController()
    setError(null)
    setParameters(null)
    listParameters(ruleSetId, controller.signal)
      .then((page) => {
        if (controller.signal.aborted) return
        setParameters(page.items)
        setSelectedKey(page.items[0]?.key ?? null)
      })
      .catch((err) => {
        if (controller.signal.aborted) return
        if (err instanceof ApiError) setError(err.problem.detail ?? err.message)
        else setError('Could not load parameters for this RuleSet.')
      })
    return () => controller.abort()
  }, [ruleSetId])

  function updateRow(updated: ParameterView) {
    setParameters((prev) => (prev ? prev.map((p) => (p.id === updated.id ? updated : p)) : prev))
  }

  function violationsText(err: unknown, fallback: string): string {
    if (err instanceof ApiError) {
      const violations = err.problem.violations
      if (violations && violations.length > 0) {
        return violations.map((v) => v.message ?? v.code).join('; ')
      }
      return err.problem.detail ?? err.message
    }
    return fallback
  }

  async function handleTransition(row: ParameterView, action: 'review' | 'approve' | 'publish') {
    setBusyRowId(row.id)
    setRowError(null)
    try {
      const fn = action === 'review' ? submitParameterForReview : action === 'approve' ? approveParameter : publishParameter
      const updated = await fn(row.id, { comment: null })
      updateRow(updated)
    } catch (err) {
      setRowError(violationsText(err, `Could not ${action} this parameter version.`))
    } finally {
      setBusyRowId(null)
    }
  }

  const keys = useMemo(() => Array.from(new Set((parameters ?? []).map((p) => p.key))), [parameters])
  const laneRows = useMemo(
    () =>
      (parameters ?? [])
        .filter((p) => p.key === selectedKey)
        .slice()
        .sort((a, b) => a.validFrom.localeCompare(b.validFrom)),
    [parameters, selectedKey],
  )

  const asOfResolution = useMemo(() => {
    // Mirrors CartridgeResolverImpl's real semantics: findEffective ... AND status='published' — a
    // draft segment the as-of marker overlaps still does not resolve.
    const published = laneRows.filter((p) => p.status === 'published')
    const hit = published.find((p) => p.validFrom <= asOf && (p.validTo === null || asOf < p.validTo))
    if (hit) return { kind: 'published' as const, row: hit }
    const draftHit = laneRows.find(
      (p) => p.status !== 'published' && p.validFrom <= asOf && (p.validTo === null || asOf < p.validTo),
    )
    if (draftHit) return { kind: 'draft-only' as const, row: draftHit }
    return { kind: 'none' as const, row: null }
  }, [laneRows, asOf])

  return (
    <div className="iw-param-editor">
      <div className="iw-param-editor__header">
        <span className="iw-param-editor__title">Parameters</span>
        <button type="button" className="iw-btn iw-btn--sm" onClick={() => setDialogOpen(true)} disabled={!parameters}>
          <Icon name="plus" size={13} />
          New parameter version
        </button>
      </div>

      {error ? (
        <InlineAlert severity="error">{error}</InlineAlert>
      ) : rowError ? (
        <InlineAlert severity="error">{rowError}</InlineAlert>
      ) : null}

      {parameters === null && !error ? (
        <div className="iw-param-editor__empty">Loading…</div>
      ) : parameters && parameters.length === 0 ? (
        <div className="iw-param-editor__empty">No parameters yet for this RuleSet — create the first version.</div>
      ) : (
        <table className="iw-param-editor__table" role="grid" aria-label="Parameters">
          <thead>
            <tr>
              <th scope="col">Key</th>
              <th scope="col">Value</th>
              <th scope="col">Valid from</th>
              <th scope="col">Valid to</th>
              <th scope="col">Status</th>
              <th scope="col">
                Statutory ref{' '}
                <span title="Not part of the Parameter record. See the RuleSet or a related Decision's citation where available.">
                  <Icon name="info" size={11} />
                </span>
              </th>
              <th scope="col" aria-label="Actions" />
            </tr>
          </thead>
          <tbody>
            {parameters?.map((row) => (
              <tr
                key={row.id}
                className={row.key === selectedKey ? 'iw-param-editor__row iw-param-editor__row--selected' : 'iw-param-editor__row'}
                onClick={() => setSelectedKey(row.key)}
              >
                <td>
                  <code style={{ fontFamily: 'var(--iw-font-mono)' }}>{row.key}</code>
                </td>
                <td>
                  <code style={{ fontFamily: 'var(--iw-font-mono)', fontSize: 'var(--iw-size-xs)' }}>{JSON.stringify(row.value)}</code>
                </td>
                <td>{row.validFrom}</td>
                <td>{row.validTo ?? 'open'}</td>
                <td>
                  <StatusPill {...lifecycleStatusPill(row.status)} />
                </td>
                <td>
                  <ForwardLookingCell reason="ParameterView carries no citation field — see the RuleSet or a related Decision's citation." />
                </td>
                <td onClick={(e) => e.stopPropagation()}>
                  <RowActionsMenu label={`Actions for parameter ${row.key} (${row.validFrom})`} actions={buildParameterRowActions(row, busyRowId, handleTransition)} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {selectedKey && laneRows.length > 0 ? (
        <div className="iw-param-lane">
          <div className="iw-param-lane__header">
            <span className="iw-param-lane__title">Effective-date lane — {selectedKey}</span>
            <label className="iw-param-lane__asof">
              As of{' '}
              <input type="date" className="iw-input" value={asOf} onChange={(e) => setAsOf(e.target.value)} />
            </label>
          </div>
          <div className="iw-param-lane__track">
            {laneRows.map((row) => (
              <span
                key={row.id}
                className={row.status === 'published' ? 'iw-param-lane__seg iw-param-lane__seg--pub' : 'iw-param-lane__seg iw-param-lane__seg--draft'}
                title={`${row.status} · ${row.validFrom} → ${row.validTo ?? 'open'}`}
              >
                {row.validFrom} → {row.validTo ?? 'open'}
              </span>
            ))}
          </div>
          <p className="iw-param-lane__resolution">
            {asOfResolution.kind === 'published'
              ? `Resolves to the published version valid ${asOfResolution.row!.validFrom} → ${asOfResolution.row!.validTo ?? 'open'}.`
              : asOfResolution.kind === 'draft-only'
                ? `A ${asOfResolution.row!.status} version covers ${asOf}, but does not resolve until published (mirrors CartridgeResolverImpl: only status='published' rows resolve).`
                : `No published version covers ${asOf}.`}
          </p>
        </div>
      ) : null}

      {keys.length > 0 ? (
        <div className="iw-param-editor__keys">
          <span className="iw-field-label">Keys:</span>
          {keys.map((k) => (
            <button
              key={k}
              type="button"
              className={k === selectedKey ? 'iw-pill iw-pill--info' : 'iw-pill iw-pill--muted'}
              onClick={() => setSelectedKey(k)}
            >
              {k}
            </button>
          ))}
        </div>
      ) : null}

      {dialogOpen ? (
        <NewParameterDialog
          ruleSetId={ruleSetId}
          onClose={() => setDialogOpen(false)}
          onCreated={(created) => {
            setDialogOpen(false)
            setParameters((prev) => [created, ...(prev ?? [])])
            setSelectedKey(created.key)
          }}
        />
      ) : null}
    </div>
  )
}

function NewParameterDialog({
  ruleSetId,
  onClose,
  onCreated,
}: {
  ruleSetId: string
  onClose: () => void
  onCreated: (row: ParameterView) => void
}) {
  const [key, setKey] = useState('')
  const [value, setValue] = useState('0')
  const [validFrom, setValidFrom] = useState(() => new Date().toISOString().slice(0, 10))
  const [validTo, setValidTo] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const keyRef = useRef<HTMLInputElement>(null)

  let parsedValue: unknown = null
  let valueJsonError = false
  try {
    parsedValue = JSON.parse(value)
  } catch {
    valueJsonError = true
  }

  const isValid = key.trim() !== '' && validFrom !== '' && !valueJsonError

  async function handleSubmit() {
    if (!isValid || submitting) return
    setSubmitting(true)
    setError(null)
    try {
      const created = await createParameterDraft(ruleSetId, {
        key: key.trim(),
        value: parsedValue,
        validFrom,
        validTo: validTo || null,
      })
      onCreated(created)
    } catch (err) {
      const violations = err instanceof ApiError ? err.problem.violations : undefined
      const message =
        violations && violations.length > 0
          ? violations.map((v) => v.message ?? v.code).join('; ')
          : err instanceof ApiError
            ? (err.problem.detail ?? err.message)
            : 'Could not create this parameter version.'
      setError(message)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <FocusTrapDialog
      titleId="new-parameter-title"
      title="New parameter version"
      onClose={onClose}
      initialFocusRef={keyRef}
      footer={
        <>
          <button type="button" className="iw-btn" onClick={onClose} disabled={submitting}>
            Cancel
          </button>
          <button type="button" className="iw-btn iw-btn--primary" onClick={() => void handleSubmit()} disabled={!isValid || submitting}>
            {submitting ? 'Creating…' : 'Create draft'}
          </button>
        </>
      }
    >
      <form
        onSubmit={(e) => {
          e.preventDefault()
          void handleSubmit()
        }}
        style={{ display: 'flex', flexDirection: 'column', gap: 'var(--iw-space-4)' }}
      >
        {error ? <InlineAlert severity="error">{error}</InlineAlert> : null}
        <div className="iw-field">
          <label className="iw-field-label" htmlFor="new-param-key">
            Key
          </label>
          <input id="new-param-key" ref={keyRef} className="iw-input" value={key} onChange={(e) => setKey(e.target.value)} placeholder="e.g. creditRatePct" />
        </div>
        <div className="iw-field">
          <label className="iw-field-label" htmlFor="new-param-value">
            Value (JSON)
          </label>
          <textarea
            id="new-param-value"
            className="iw-input"
            rows={3}
            style={{ fontFamily: 'var(--iw-font-mono)' }}
            value={value}
            onChange={(e) => setValue(e.target.value)}
          />
          {valueJsonError ? <span style={{ color: 'var(--iw-danger)', fontSize: 'var(--iw-size-xs)' }}>Must be valid JSON.</span> : null}
        </div>
        <div className="iw-field">
          <label className="iw-field-label" htmlFor="new-param-valid-from">
            Valid from
          </label>
          <input id="new-param-valid-from" className="iw-input" type="date" value={validFrom} onChange={(e) => setValidFrom(e.target.value)} />
        </div>
        <div className="iw-field">
          <label className="iw-field-label" htmlFor="new-param-valid-to">
            Valid to (optional — leaves the window open)
          </label>
          <input id="new-param-valid-to" className="iw-input" type="date" value={validTo} onChange={(e) => setValidTo(e.target.value)} />
        </div>
      </form>
    </FocusTrapDialog>
  )
}

function buildParameterRowActions(
  row: ParameterView,
  busyRowId: string | null,
  handleTransition: (row: ParameterView, action: 'review' | 'approve' | 'publish') => void,
): RowAction[] {
  if (busyRowId === row.id) return []
  const actions: RowAction[] = []
  if (row.status === 'draft') {
    actions.push({ id: 'review', label: 'Submit for review', icon: 'check', onSelect: () => handleTransition(row, 'review') })
  } else if (row.status === 'review') {
    actions.push({ id: 'approve', label: 'Approve', icon: 'check-circle', onSelect: () => handleTransition(row, 'approve') })
  } else if (row.status === 'approved') {
    actions.push({ id: 'publish', label: 'Publish', icon: 'check-circle', onSelect: () => handleTransition(row, 'publish') })
  }
  return actions
}
